// SPDX-License-Identifier: AGPL-3.0-or-later

// KONVO: Outlook mail store — folders, paging, server search, full messages
// with attachments, read/flag/move mutations, compose/reply/forward, and a
// people lookup for the recipient picker.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Activity from '@app/features/konvo_activity/state/Activity';
import {
	composeMailSearch,
	hasMailSearchFilters,
	type MailSearchFilters,
	mailSearchWords,
	NO_MAIL_SEARCH_FILTERS,
} from '@app/features/konvo_mail/state/MailSearchTerms';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {afterStartup} from '@app/features/platform/utils/StartupScheduler';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const logger = new Logger('KonvoMail');
const REFRESH_INTERVAL_MS = 3 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 350;
const DETAIL_PREFETCH_LIMIT = 8;
const DETAIL_PREFETCH_CONCURRENCY = 2;
const DETAIL_CACHE_LIMIT = 40;
const DETAIL_CACHE_BODY_CHARS = 8 * 1024 * 1024;
/** How long a swiped-away message waits before it really moves, so it can be undone. */
export const MOVE_UNDO_MS = 5000;
/** How long a list fetched around a read/unread write may still carry the old state. */
const READ_SETTLE_MS = 15_000;
/** Moves that come due within this window go to the server together. */
const MOVE_BATCH_WINDOW_MS = 30;
/** Ids per bulk move request (the server accepts up to 50). */
const MOVE_BATCH_SIZE = 25;

/** `custom`: one of the person's own Outlook folders (folderId / folderName say which). */
export type KonvoMailFolder = 'inbox' | 'sent' | 'drafts' | 'archive' | 'deleted' | 'junk' | 'custom';
export type KonvoMailView = 'all' | 'unread' | 'flagged' | 'attachments';
export const KONVO_MAIL_FOLDERS: ReadonlyArray<KonvoMailFolder> = ['inbox', 'sent', 'drafts', 'archive', 'deleted'];

export interface KonvoMailAddress {
	name: string | null;
	address: string;
}

export interface KonvoMailAttachment {
	id: string;
	name: string;
	content_type: string;
	size: number;
	is_inline: boolean;
}

export interface KonvoMailMessage {
	id: string;
	subject: string;
	from_name: string | null;
	from_address: string | null;
	to: Array<KonvoMailAddress>;
	received_at: string | null;
	is_read: boolean;
	is_draft: boolean;
	is_flagged: boolean;
	preview: string;
	web_link: string | null;
	importance: string | null;
	has_attachments: boolean;
	conversation_id: string | null;
	/** Pinned in Konvo: listed first in its folder. Absent from routes that don't track pins. */
	is_pinned?: boolean;
	/** An Outlook invite's meeting time, for the row's time chip. Only on list routes. */
	event?: KonvoMailListEvent;
	/** Inbox only: mail to glance at, no reply needed (konvo_api KonvoMailFyi). */
	fyi?: KonvoMailFyi | null;
	/** Inbox only: automated mail that stays, because it asks you something or a run failed. */
	attention?: KonvoMailAttention | null;
	/** Searches only: the Outlook folder the mail is in (a search covers the whole mailbox). */
	folder_id?: string | null;
	/** Top results only: where the words matched, with each match's [start, end) in `text`. */
	snippet?: {text: string; marks: Array<[number, number]>} | null;
}

export type KonvoMailFyiKind = 'notification' | 'run' | 'digest' | 'meeting_reply';

export interface KonvoMailFyi {
	kind: KonvoMailFyiKind;
	/** Card group and rule key: `runs`, `digests`, `meeting_replies` or `n:<domain>`. */
	group: string;
	source: string;
	topic: string | null;
	actor: string | null;
}

export interface KonvoMailAttention {
	kind: 'asks_you' | 'failed';
	source: string;
	topic: string | null;
	actor: string | null;
}

/** The meeting an invite in the list is for; times are ISO instants. */
export interface KonvoMailListEvent {
	start: string | null;
	end: string | null;
	is_all_day: boolean;
	is_cancelled: boolean;
	/** Your reply: none, notResponded, accepted, tentativelyAccepted, declined or organizer. */
	response: string;
}

export interface KonvoMailMessageFiles extends KonvoMailMessage {
	attachments: Array<KonvoMailAttachment>;
}

/** An Outlook folder, for Move to folder. `well_known` names the standard ones. */
export interface KonvoMailFolderInfo {
	id: string;
	name: string;
	parent_id: string | null;
	well_known: string | null;
	unread_count: number;
	total_count: number;
	depth: number;
}

/** Where a move sends a message: a standard folder, or any Outlook folder by id. */
export type KonvoMailMoveTarget = KonvoMailFolder | {folderId: string};

export interface KonvoMailDetail extends KonvoMailMessage {
	reply_to: Array<KonvoMailAddress>;
	cc: Array<KonvoMailAddress>;
	bcc: Array<KonvoMailAddress>;
	sent_at: string | null;
	body_type: 'html' | 'text';
	body: string;
	attachments: Array<KonvoMailAttachment>;
	/** An Outlook invite, a reply to one, or a cancellation. Absent from older servers. */
	meeting_message?: boolean;
}

export interface KonvoMailContact extends KonvoMailAddress {
	source: string;
}

export interface KonvoMailOutgoingAttachment {
	name: string;
	content_type: string;
	content_base64: string;
	size: number;
}

export type KonvoMailComposeMode = 'new' | 'reply' | 'reply_all' | 'forward';

export interface KonvoMailSendRequest {
	to: Array<KonvoMailAddress>;
	cc: Array<KonvoMailAddress>;
	bcc: Array<KonvoMailAddress>;
	subject: string;
	body_html: string;
	attachments: Array<KonvoMailOutgoingAttachment>;
	/** Reply / forward only: false drops the quoted original from the sent message. */
	include_quote?: boolean;
}

/** The draft's contents; the server keeps Graph's quoted original under it for replies. */
export interface KonvoMailDraftRequest {
	to: Array<KonvoMailAddress>;
	cc: Array<KonvoMailAddress>;
	bcc: Array<KonvoMailAddress>;
	subject: string;
	body_html: string;
	include_quote?: boolean;
}

/** An Outlook draft as the composer continues it: the quoted original stays in Outlook. */
export interface KonvoMailSavedDraft {
	id: string;
	subject: string;
	to: Array<KonvoMailAddress>;
	cc: Array<KonvoMailAddress>;
	bcc: Array<KonvoMailAddress>;
	body_html: string;
	/** A reply inside a thread: the quote sits under body_html in Outlook. */
	in_thread: boolean;
	attachments: Array<{id: string; name: string; size: number; content_type: string}>;
}

export interface KonvoMailDraftCreateRequest extends KonvoMailDraftRequest {
	mode: KonvoMailComposeMode;
	original_id?: string;
}

export interface KonvoMailComposeAssistRequest {
	mode: KonvoMailComposeMode;
	original_id?: string;
	to: Array<KonvoMailAddress>;
	cc: Array<KonvoMailAddress>;
	subject: string;
	notes_text: string;
	/** Change the current body (notes_text) as asked, keeping the rest. */
	instruction?: string;
}

type PinResult = 'ok' | 'too_many' | 'failed';

export type KonvoMailResult<T> = {ok: true; value: T} | {ok: false; error: string; status: number};

/** Runs a mail request and reads `{ok, error}` bodies the same way for every draft call. */
async function mailCall<T>(
	request: () => Promise<{status: number; body: unknown}>,
	read: (body: Record<string, unknown>) => T | null,
): Promise<KonvoMailResult<T>> {
	try {
		const response = await request();
		const body = (response.body ?? {}) as Record<string, unknown>;
		if (response.status >= 200 && response.status < 300 && body['error'] == null) {
			const value = read(body);
			if (value != null) return {ok: true, value};
		}
		return {
			ok: false,
			error: typeof body['error'] === 'string' ? body['error'] : `http_${response.status}`,
			status: response.status,
		};
	} catch (error) {
		logger.warn('Mail request failed', error);
		return {ok: false, error: 'network', status: 0};
	}
}

/** A read/unread choice that a list fetched before Outlook applied it must not undo. */
interface ReadEdit {
	isRead: boolean;
	/** When the write succeeded plus READ_SETTLE_MS; null while it is in flight. */
	until: number | null;
	/** What the last list applied to the inbox total for this edit, to undo if the write fails. */
	countApplied: number;
}

interface PendingMove {
	timer: ReturnType<typeof setTimeout>;
	commit: () => Promise<void>;
}

interface MoveBatch {
	target: KonvoMailMoveTarget;
	done: Map<string, (moved: boolean) => void>;
}

interface ListResponse {
	error?: string;
	connected: boolean;
	needs_consent?: boolean;
	unread_count: number;
	messages: Array<KonvoMailMessage>;
	has_more?: boolean;
	next_skip?: number;
	/** Searches: where the next page of results starts. */
	next_cursor?: string | null;
	folder?: string;
	folder_id?: string;
	folder_name?: string | null;
	folder_unread_count?: number | null;
	view?: KonvoMailView;
}

/** Graph's well-known folder names → the Mail page's own folders. */
const WELL_KNOWN_TO_FOLDER: Record<string, KonvoMailFolder> = {
	inbox: 'inbox',
	sentitems: 'sent',
	drafts: 'drafts',
	archive: 'archive',
	deleteditems: 'deleted',
	junkemail: 'junk',
};

/** The person's own folders for the folder list: every folder except the fixed ones (and Outbox). */
export function ownMailFolders(folders: ReadonlyArray<KonvoMailFolderInfo>): Array<KonvoMailFolderInfo> {
	return folders.filter((folder) => folder.well_known == null);
}

/** "Clients › Lore": a folder's name after its parents' (fixed folders such as Inbox included). */
export function mailFolderPath(folder: KonvoMailFolderInfo, folders: ReadonlyArray<KonvoMailFolderInfo>): string {
	const byId = new Map(folders.map((entry) => [entry.id, entry]));
	const names = [folder.name];
	let parent = folder.parent_id ? byId.get(folder.parent_id) : undefined;
	for (let guard = 0; parent && guard < 8; guard++) {
		names.unshift(parent.name);
		parent = parent.parent_id ? byId.get(parent.parent_id) : undefined;
	}
	return names.join(' › ');
}

/** Whether a message belongs in a Mail page filter (Unread, Flagged, With attachments). */
export function matchesMailView(message: KonvoMailMessage, view: KonvoMailView): boolean {
	if (view === 'unread') return !message.is_read;
	if (view === 'flagged') return message.is_flagged;
	if (view === 'attachments') return message.has_attachments;
	return true;
}

export function formatMailAddress(entry: KonvoMailAddress): string {
	return entry.name ? `${entry.name} <${entry.address}>` : entry.address;
}

export class KonvoMailStore {
	folder: KonvoMailFolder = 'inbox';
	/** With folder 'custom': which of the person's own folders is open, and its name. */
	folderId: string | null = null;
	folderName: string | null = null;
	view: KonvoMailView = 'all';
	messages: Array<KonvoMailMessage> = [];
	/** The last complete inbox, independent of the Mail page's folder and search. */
	private inboxSnapshot: Array<KonvoMailMessage> | null = null;
	unreadCount = 0;
	connected: boolean | null = null;
	needsConsent = false;
	fetched = false;
	/** The last list request failed (network or server); the page offers a retry. */
	error = false;
	/** Message id whose detail request failed, so the reading pane can offer a retry. */
	detailErrorId: string | null = null;
	loading = false;
	loadingMore = false;
	/** The last older-mail request failed; the list offers a button instead of loading on scroll. */
	loadMoreFailed = false;
	hasMore = false;
	nextSkip = 0;
	query = '';
	/** Query the current list was fetched for (server search). */
	activeQuery = '';
	/** A typed search is waiting out the debounce before its request starts. */
	private searchQueued = false;
	/** Outlook's Top results for activeQuery (by relevance), shown above the newest-first results. */
	topResults: Array<KonvoMailMessage> = [];
	/** The chips under the search box: sender, attachments, unread, date and one folder. */
	searchFilters: MailSearchFilters = NO_MAIL_SEARCH_FILTERS;
	/** The typed words of the search on screen (activeQuery adds the chips' keywords). */
	activeText = '';
	/** A search that found nothing: the same words spelt as Konvo's index knows them. */
	didYouMean: string | null = null;
	/** The search on screen: its words and the folder it was kept to. */
	private activeSearchKey = '';
	private activeScope: string | null = null;
	/** Where the next page of the current search starts. */
	private searchCursor: string | null = null;
	private topRequest = 0;
	selectedId: string | null = null;
	/** The phone list is picking messages; its action bar takes the rail's place. */
	selecting = false;
	details = observable.map<string, KonvoMailDetail>();
	/** Conversation id → its messages across folders, oldest first, your sent replies included. */
	conversations = observable.map<string, Array<KonvoMailMessage>>();
	/** Conversation id → the messages of it that carry files, each with its attachment list. */
	conversationFiles = observable.map<string, Array<KonvoMailMessageFiles>>();
	detailLoadingId: string | null = null;
	/** Message id → render the sender's original colours instead of the theme-adapted view. */
	originalColours = observable.set<string>();
	/** Outlook folders for Move to folder and the Folders menu; null until first asked for. */
	folders: Array<KonvoMailFolderInfo> | null = null;
	foldersLoading = false;
	private foldersLoadedAt = 0;
	private refreshTimer: VisiblePoll | null = null;
	private searchTimer: ReturnType<typeof setTimeout> | null = null;
	private changeTimer: ReturnType<typeof setTimeout> | null = null;
	private changePending = false;
	private changeRefreshing = false;
	private listRequest = 0;
	/** The list (folder and view) that messages, hasMore and nextSkip were paged for. */
	private pagedList: string | null = null;
	private unreadCountVersion = 0;
	/** Invalidates list responses across pin edits, move starts, completions and undo. */
	private listRevision = 0;
	private pendingPinChanges = 0;
	private pinWrite: Promise<unknown> | null = null;
	private pinEdits = new Map<string, {revision: number; confirmed: boolean}>();
	private readEdits = new Map<string, ReadEdit>();
	private pendingMoves = new Map<string, PendingMove>();
	private movingMessages = new Set<string>();
	private moveBatches = new Map<string, MoveBatch>();
	private moveBatchTimer: ReturnType<typeof setTimeout> | null = null;
	private detailRequests = new Map<string, Promise<KonvoMailDetail | null>>();
	private detailQueue: Array<string> = [];
	private detailPrefetching = 0;
	private cancelDeferredDrain: (() => void) | null = null;

	constructor() {
		makeAutoObservable<
			this,
			| 'refreshTimer'
			| 'searchTimer'
			| 'changeTimer'
			| 'changePending'
			| 'changeRefreshing'
			| 'listRequest'
			| 'topRequest'
			| 'unreadCountVersion'
			| 'listRevision'
			| 'pendingPinChanges'
			| 'pinWrite'
			| 'pinEdits'
			| 'readEdits'
			| 'pendingMoves'
			| 'movingMessages'
			| 'moveBatches'
			| 'moveBatchTimer'
			| 'detailRequests'
			| 'detailQueue'
			| 'detailPrefetching'
			| 'cancelDeferredDrain'
		>(
			this,
			{
				refreshTimer: false,
				searchTimer: false,
				changeTimer: false,
				changePending: false,
				changeRefreshing: false,
				listRequest: false,
				topRequest: false,
				unreadCountVersion: false,
				listRevision: false,
				pendingPinChanges: false,
				pinWrite: false,
				pinEdits: false,
				readEdits: false,
				pendingMoves: false,
				movingMessages: false,
				moveBatches: false,
				moveBatchTimer: false,
				detailRequests: false,
				detailQueue: false,
				detailPrefetching: false,
				cancelDeferredDrain: false,
			},
			{autoBind: true},
		);
		if (typeof document !== 'undefined') {
			// A backgrounded tab may never wake to run the timers, so send waiting moves now.
			reaction(
				() => Window.visible,
				(visible) => {
					if (!visible) this.flushPendingMoves();
					else {
						this.drainDetailQueue();
						if (this.changePending) this.handleMailChanged();
					}
				},
			);
		}
	}

	setQuery(value: string): void {
		this.query = value;
		if (value.trim() && this.view !== 'all') this.view = 'all';
		this.queueSearch(SEARCH_DEBOUNCE_MS);
	}

	/** Changes the search's chips; the search runs again at once. */
	setSearchFilters(patch: Partial<MailSearchFilters>): void {
		this.searchFilters = {...this.searchFilters, ...patch};
		if (this.view !== 'all') this.view = 'all';
		this.queueSearch(0);
	}

	/** Empties the search box and its chips: back to the folder. */
	clearSearch(): void {
		this.query = '';
		this.searchFilters = NO_MAIL_SEARCH_FILTERS;
		this.queueSearch(0);
	}

	private queueSearch(delayMs: number): void {
		if (this.searchTimer) clearTimeout(this.searchTimer);
		this.searchQueued = true;
		this.searchTimer = setTimeout(() => {
			runInAction(() => {
				this.searchTimer = null;
				this.searchQueued = false;
			});
			void this.refresh();
		}, delayMs);
	}

	/** What Outlook is asked for: the typed words plus a keyword per chip. */
	get composedQuery(): string {
		return composeMailSearch(this.query, this.searchFilters);
	}

	/** The folder a search is kept to, if any. */
	private get searchScope(): string | null {
		return this.composedQuery ? (this.searchFilters.folder?.id ?? null) : null;
	}

	private get searchKey(): string {
		const query = this.composedQuery;
		return query ? `${query}|${this.searchScope ?? ''}` : '';
	}

	/** The list still shows another search (or none) than the box names, and Konvo is fetching the new one. */
	get searchPending(): boolean {
		return this.searchKey !== this.activeSearchKey && (this.searchQueued || this.loading);
	}

	/** The words of the search on screen, to mark in its results. */
	get searchWords(): Array<string> {
		return mailSearchWords(this.activeText);
	}

	/** A search that found nothing asks Konvo's index how its words are spelt in the mail. */
	private async loadDidYouMean(text: string): Promise<void> {
		try {
			const response = await http.get<{did_you_mean?: string | null}>(Endpoints.KONVO_MAIL_SEARCH_SUGGEST, {
				query: {q: text},
			});
			runInAction(() => {
				if (text === this.activeText && this.messages.length === 0)
					this.didYouMean = response.body?.did_you_mean ?? null;
			});
		} catch (error) {
			logger.warn('Failed to load the mail spelling suggestion', error);
		}
	}

	/** A search result's folder name, once the folder list is in. */
	resultFolderName(folderId: string | null | undefined): string | null {
		if (!folderId) return null;
		return this.folders?.find((folder) => folder.id === folderId)?.name ?? null;
	}

	/** Outlook's Top results, fetched beside the list; best effort, so a failure just leaves them off. */
	private async loadTopResults(query: string): Promise<void> {
		const request = ++this.topRequest;
		if (!query) {
			this.topResults = [];
			return;
		}
		try {
			const response = await http.get<{messages?: Array<KonvoMailMessage>}>(Endpoints.KONVO_MAIL_SEARCH_TOP, {
				query: {q: query},
				...RETRY_RATE_LIMITS,
			});
			if (request !== this.topRequest) return;
			runInAction(() => {
				this.topResults = this.withReadEdits(this.withoutPendingMoves(response.body?.messages ?? []));
			});
		} catch (error) {
			logger.warn('Failed to load top mail results', error);
			if (request === this.topRequest)
				runInAction(() => {
					this.topResults = [];
				});
		}
	}

	setFolder(folder: KonvoMailFolder): void {
		if (folder === 'custom' || (folder === this.folder && this.view === 'all')) return;
		this.folder = folder;
		this.folderId = null;
		this.folderName = null;
		this.view = 'all';
		this.resetList();
		void this.refresh();
	}

	/** Opens one of the person's Outlook folders in the list (a fixed one opens as its own tab). */
	openFolder(info: Pick<KonvoMailFolderInfo, 'id' | 'name' | 'well_known'>): void {
		const fixed = info.well_known ? WELL_KNOWN_TO_FOLDER[info.well_known] : undefined;
		if (fixed) {
			this.setFolder(fixed);
			return;
		}
		if (this.folder === 'custom' && this.folderId === info.id && this.view === 'all' && !this.composedQuery) return;
		if (this.searchTimer) clearTimeout(this.searchTimer);
		this.searchTimer = null;
		this.searchQueued = false;
		this.folder = 'custom';
		this.folderId = info.id;
		this.folderName = info.name;
		this.view = 'all';
		this.query = '';
		this.activeQuery = '';
		this.activeText = '';
		this.activeSearchKey = '';
		this.activeScope = null;
		this.searchFilters = NO_MAIL_SEARCH_FILTERS;
		this.topResults = [];
		this.didYouMean = null;
		this.resetList();
		void this.refresh();
	}

	/** Which list is on screen: a folder name, or the id of one of the person's own folders. */
	private get listKey(): string {
		return this.folder === 'custom' ? `custom:${this.folderId}` : this.folder;
	}

	/** The folder the list request names: a fixed folder, or folder_id for one of the person's own. */
	private get folderQuery(): {folder: string; folder_id?: string} {
		return this.folder === 'custom' && this.folderId
			? {folder: 'custom', folder_id: this.folderId}
			: {folder: this.folder};
	}

	setView(view: KonvoMailView): void {
		if (view === this.view && this.folder === 'inbox' && !this.composedQuery) return;
		if (this.searchTimer) clearTimeout(this.searchTimer);
		this.searchTimer = null;
		this.searchQueued = false;
		this.folder = 'inbox';
		this.folderId = null;
		this.folderName = null;
		this.view = view;
		this.query = '';
		this.activeQuery = '';
		this.activeText = '';
		this.activeSearchKey = '';
		this.activeScope = null;
		this.searchFilters = NO_MAIL_SEARCH_FILTERS;
		this.topResults = [];
		this.didYouMean = null;
		this.resetList();
		// The inbox already loaded answers a filter at once; Outlook only adds older matches behind it.
		const inbox = this.inboxSnapshot;
		if (!inbox) {
			void this.refresh();
			return;
		}
		this.messages = inbox.filter((message) => matchesMailView(message, view));
		// Every unread mail is already here when the inbox count says so: nothing to ask Outlook.
		if (view === 'unread' && this.messages.length >= this.unreadCount) return;
		void this.refresh({silent: true});
	}

	private resetList(): void {
		this.selectedId = null;
		this.messages = [];
		this.hasMore = false;
		this.nextSkip = 0;
		this.loadMoreFailed = false;
		this.pagedList = null;
	}

	/** Folder and filter: older pages only belong behind a first page of the same list. */
	private get pagingKey(): string {
		return `${this.listKey}|${this.view}`;
	}

	/**
	 * Older mail already paged in behind the first page: what came after the last mail of the new
	 * first page that was already listed. A refresh keeps it, so the list doesn't shrink back.
	 */
	private olderTail(page: Array<KonvoMailMessage>): Array<KonvoMailMessage> {
		const ids = new Set(page.map((message) => message.id));
		for (let index = page.length - 1; index >= 0; index--) {
			const message = page[index]!;
			if (message.is_pinned) continue;
			const at = this.messages.findIndex((entry) => entry.id === message.id);
			if (at >= 0) return this.messages.slice(at + 1).filter((entry) => !ids.has(entry.id));
		}
		return [];
	}

	get inboxMessages(): Array<KonvoMailMessage> {
		return (
			this.inboxSnapshot ?? (this.folder === 'inbox' && this.view === 'all' && !this.activeQuery ? this.messages : [])
		);
	}

	private summaryFor(messageId: string): KonvoMailMessage | undefined {
		return (
			this.messages.find((message) => message.id === messageId) ??
			this.topResults.find((message) => message.id === messageId) ??
			this.inboxMessages.find((message) => message.id === messageId)
		);
	}

	private rememberInbox(): void {
		if (this.folder === 'inbox' && this.view === 'all' && !this.query.trim() && !this.activeQuery)
			this.inboxSnapshot = [...this.messages];
	}

	setSelecting(selecting: boolean): void {
		this.selecting = selecting;
	}

	/**
	 * Chats opens an inbox message even if the Mail page last showed another folder or filter.
	 * Its row stands for the whole conversation and stays unread while any of it is, so the
	 * rest of the conversation is read with it, as a Mail page conversation row does.
	 */
	selectInbox(messageId: string): void {
		this.setView('all');
		this.select(messageId);
		const opened = this.summaryFor(messageId);
		if (!opened?.conversation_id || opened.is_draft) return;
		const unread = this.inboxMessages.filter(
			(message) =>
				message.id !== messageId &&
				message.conversation_id === opened.conversation_id &&
				!message.is_read &&
				!message.is_draft,
		);
		for (const message of unread) void this.markRead(message.id, true);
	}

	select(messageId: string | null): void {
		this.selectedId = messageId;
		if (!messageId) return;
		void this.loadDetail(messageId);
		const summary = this.summaryFor(messageId);
		if (summary && !summary.is_read && !summary.is_draft) void this.markRead(messageId, true);
	}

	toggleOriginalColours(messageId: string): void {
		if (this.originalColours.has(messageId)) this.originalColours.delete(messageId);
		else this.originalColours.add(messageId);
	}

	/** The listed mail, or one opened from a row's dropdown that sits in another folder (a reply you sent). */
	get selectedSummary(): KonvoMailMessage | null {
		const id = this.selectedId;
		if (!id) return null;
		const listed = this.summaryFor(id);
		if (listed) return listed;
		for (const messages of this.conversations.values()) {
			const found = messages.find((message) => message.id === id);
			if (found) return found;
		}
		return null;
	}

	get selectedDetail(): KonvoMailDetail | null {
		return this.selectedId ? (this.details.get(this.selectedId) ?? null) : null;
	}

	/** One conversation across inbox, sent and archive, for reading it as a thread. */
	async loadConversation(conversationId: string): Promise<void> {
		try {
			const response = await http.get<{messages?: Array<KonvoMailMessage>}>(
				Endpoints.KONVO_MAIL_CONVERSATION(conversationId),
				{mode: 'silent'},
			);
			const messages = response.body?.messages;
			if (Array.isArray(messages)) {
				runInAction(() => {
					this.conversations.set(conversationId, messages);
				});
			}
		} catch (error) {
			logger.warn('Failed to load mail conversation', error);
		}
	}

	/** Every file sent in a conversation, names and sizes only; the bytes load when one is opened. */
	async loadConversationFiles(conversationId: string): Promise<void> {
		try {
			const response = await http.get<{messages?: Array<KonvoMailMessageFiles>}>(
				Endpoints.KONVO_MAIL_CONVERSATION_ATTACHMENTS(conversationId),
				{mode: 'silent'},
			);
			const messages = response.ok ? response.body?.messages : undefined;
			if (Array.isArray(messages)) {
				runInAction(() => {
					this.conversationFiles.set(conversationId, messages);
				});
			}
		} catch (error) {
			logger.warn('Failed to load mail conversation files', error);
		}
	}

	async loadDetail(messageId: string, force = false, background = false): Promise<KonvoMailDetail | null> {
		const cached = this.details.get(messageId);
		if (!force && cached) return cached;
		if (!background) {
			this.detailLoadingId = messageId;
			if (this.detailErrorId === messageId) this.detailErrorId = null;
		}
		let request = this.detailRequests.get(messageId);
		if (!request) {
			request = this.fetchDetail(messageId);
			this.detailRequests.set(messageId, request);
		}
		const detail = await request;
		if (this.detailRequests.get(messageId) === request) this.detailRequests.delete(messageId);
		if (!background) {
			runInAction(() => {
				if (!detail) this.detailErrorId = messageId;
				if (this.detailLoadingId === messageId) this.detailLoadingId = null;
			});
		}
		return detail;
	}

	private async fetchDetail(messageId: string): Promise<KonvoMailDetail | null> {
		try {
			const response = await http.get<KonvoMailDetail & {connected?: boolean; error?: string}>(
				Endpoints.KONVO_MAIL_MESSAGE(messageId),
				RETRY_RATE_LIMITS,
			);
			const body = response.body;
			if (!body?.id) return null;
			runInAction(() => {
				// Opening during a prefetch can mark the message read before this older response arrives.
				const summary = this.summaryFor(messageId);
				this.details.set(messageId, {
					...body,
					is_read: summary?.is_read ?? body.is_read,
					is_flagged: summary?.is_flagged ?? body.is_flagged,
					is_pinned: summary?.is_pinned ?? body.is_pinned,
					attachments: body.attachments ?? [],
					cc: body.cc ?? [],
					bcc: body.bcc ?? [],
				});
				this.trimDetails(messageId);
			});
			return this.details.get(messageId) ?? null;
		} catch (error) {
			logger.warn('Failed to load mail message', error);
			return null;
		}
	}

	private trimDetails(keepId: string): void {
		let chars = [...this.details.values()].reduce((total, detail) => total + detail.body.length, 0);
		for (const [id, detail] of this.details) {
			if (this.details.size <= DETAIL_CACHE_LIMIT && chars <= DETAIL_CACHE_BODY_CHARS) break;
			if (id === this.selectedId || id === keepId) continue;
			chars -= detail.body.length;
			this.details.delete(id);
		}
	}

	/** Warm a row on hover/focus, without selecting it or marking it read. */
	prefetchDetail(messageId: string): void {
		if (this.details.has(messageId) || this.detailRequests.has(messageId)) return;
		if (this.messages.find((message) => message.id === messageId)?.is_draft) return;
		this.detailQueue = [messageId, ...this.detailQueue.filter((id) => id !== messageId)].slice(
			0,
			DETAIL_PREFETCH_LIMIT,
		);
		this.drainDetailQueue();
	}

	private prefetchRecentDetails(): void {
		this.detailQueue = this.messages
			.filter((message) => !message.is_draft)
			.slice(0, DETAIL_PREFETCH_LIMIT)
			.map((message) => message.id);
		// The newest bodies are a convenience; in the app's first seconds they wait for it to settle.
		if (this.cancelDeferredDrain) return;
		this.cancelDeferredDrain = afterStartup(
			() => {
				this.cancelDeferredDrain = null;
				this.drainDetailQueue();
			},
			{delayMs: 2000, jitterMs: 4000},
		);
	}

	private drainDetailQueue(): void {
		if (!Window.visible) return;
		while (this.detailPrefetching < DETAIL_PREFETCH_CONCURRENCY && this.detailQueue.length > 0) {
			const id = this.detailQueue.shift()!;
			if (this.details.has(id) || this.detailRequests.has(id)) continue;
			this.detailPrefetching++;
			void this.loadDetail(id, false, true).finally(() => {
				this.detailPrefetching--;
				this.drainDetailQueue();
			});
		}
	}

	prefetch(): void {
		if (!this.fetched && !this.loading) void this.refresh();
		if (this.refreshTimer == null) {
			this.refreshTimer = new VisiblePoll(
				REFRESH_INTERVAL_MS,
				() => {
					if (!this.loading && !this.activeQuery) return this.refresh({silent: true});
					return undefined;
				},
				true,
			);
			this.refreshTimer.start();
		}
	}

	/** Coalesce bursts; remember changes while hidden, and refresh again if one arrives mid-request. */
	handleMailChanged(): void {
		this.changePending = true;
		if (!Window.visible || this.changeTimer || this.changeRefreshing) return;
		this.changeTimer = setTimeout(() => {
			this.changeTimer = null;
			if (!Window.visible) return;
			// A user's folder/search request must finish before its results are invalidated.
			if (this.loading || this.searchTimer) {
				this.handleMailChanged();
				return;
			}
			this.changePending = false;
			this.changeRefreshing = true;
			void this.refresh({silent: true}).finally(() => {
				this.changeRefreshing = false;
				if (this.changePending) this.handleMailChanged();
			});
		}, 250);
	}

	async refresh(options: {silent?: boolean} = {}): Promise<void> {
		if (this.pendingPinChanges > 0) return;
		const listRevision = this.listRevision;
		const request = ++this.listRequest;
		const query = this.composedQuery;
		const text = this.query.trim();
		const scope = this.searchScope;
		const searchKey = this.searchKey;
		if (!options.silent) this.loading = true;
		// A new search (not a background re-read of the same one) asks for its Top results too;
		// Outlook ranks words, so a search narrowed by chips lists by date only.
		if (searchKey !== this.activeSearchKey) {
			this.topResults = [];
			this.didYouMean = null;
			void this.loadTopResults(hasMailSearchFilters(this.searchFilters) ? '' : query);
		}
		if (query && this.folders == null) void this.loadFolders();
		try {
			const response = await http.get<ListResponse>(Endpoints.KONVO_MAIL_RECENT, {
				query: {
					...this.folderQuery,
					view: this.view === 'all' ? undefined : this.view,
					q: query || undefined,
					in_folder_id: scope ?? undefined,
					skip: 0,
				},
				...RETRY_RATE_LIMITS,
			});
			if (request !== this.listRequest || listRevision !== this.listRevision) return;
			if (response.body?.error) {
				runInAction(() => {
					this.needsConsent = response.body.needs_consent === true;
				});
				throw new Error(response.body.error);
			}
			runInAction(() => {
				this.connected = response.body?.connected ?? false;
				this.needsConsent = response.body?.needs_consent === true;
				const listed = this.withoutPendingMoves(response.body?.messages ?? []);
				const page = this.withReadEdits(listed);
				const pagingKey = query ? null : this.pagingKey;
				const tail =
					pagingKey != null && pagingKey === this.pagedList && response.body?.has_more === true
						? this.olderTail(page)
						: [];
				const hadMore = this.hasMore;
				const nextSkip = this.nextSkip;
				this.messages = [...page, ...tail];
				// A list fetched before Outlook finishes a move still includes its unread message.
				if (!this.hasPendingMoves()) {
					this.unreadCount = Math.max(
						0,
						(response.body?.unread_count ?? 0) + this.readEditCountDelta(listed, this.folder === 'inbox' && !query),
					);
					this.unreadCountVersion++;
				}
				this.hasMore = tail.length > 0 ? hadMore : response.body?.has_more === true;
				this.nextSkip = Math.max(response.body?.next_skip ?? page.length, tail.length > 0 ? nextSkip : 0);
				this.pagedList = pagingKey;
				if (tail.length === 0) this.loadMoreFailed = false;
				if (this.folder === 'custom' && response.body?.folder_name) this.folderName = response.body.folder_name;
				this.activeQuery = query;
				this.activeText = query ? text : '';
				this.activeScope = scope;
				this.activeSearchKey = searchKey;
				this.searchCursor = query ? (response.body?.next_cursor ?? null) : null;
				if (query && text && this.messages.length === 0 && this.didYouMean == null) void this.loadDidYouMean(text);
				this.rememberInbox();
				if (!this.connected) this.inboxSnapshot = null;
				this.fetched = true;
				this.error = false;
				for (const message of this.messages) {
					const detail = this.details.get(message.id);
					if (detail)
						this.details.set(message.id, {
							...detail,
							is_read: message.is_read,
							is_flagged: message.is_flagged,
							is_pinned: message.is_pinned,
						});
				}
			});
			this.prefetchRecentDetails();
		} catch (error) {
			logger.warn('Failed to load mail', error);
			if (request !== this.listRequest || listRevision !== this.listRevision) return;
			runInAction(() => {
				this.fetched = true;
				this.error = true;
			});
		} finally {
			runInAction(() => {
				if (request === this.listRequest) this.loading = false;
			});
		}
	}

	/** The next page of the search on screen (older matches, from every folder). */
	private async loadMoreResults(): Promise<void> {
		const query = this.activeQuery;
		const cursor = this.searchCursor;
		if (this.loadingMore || !this.hasMore || !cursor) return;
		const listRevision = this.listRevision;
		this.loadingMore = true;
		this.loadMoreFailed = false;
		try {
			const response = await http.get<ListResponse>(Endpoints.KONVO_MAIL_RECENT, {
				query: {...this.folderQuery, q: query, in_folder_id: this.activeScope ?? undefined, cursor},
				...RETRY_RATE_LIMITS,
			});
			if (query !== this.activeQuery || listRevision !== this.listRevision) return;
			if (response.body?.error) throw new Error(response.body.error);
			runInAction(() => {
				const listed = response.body?.messages ?? [];
				const known = new Set(this.messages.map((message) => message.id));
				const fresh = this.withReadEdits(this.withoutPendingMoves(listed).filter((message) => !known.has(message.id)));
				this.messages = [...this.messages, ...fresh];
				this.searchCursor = response.body?.next_cursor ?? null;
				this.hasMore = response.body?.has_more === true && this.searchCursor != null && listed.length > 0;
			});
		} catch (error) {
			logger.warn('Failed to load more search results', error);
			if (query === this.activeQuery)
				runInAction(() => {
					this.loadMoreFailed = true;
				});
		} finally {
			runInAction(() => {
				this.loadingMore = false;
			});
		}
	}

	async loadMore(): Promise<void> {
		if (this.activeQuery) return this.loadMoreResults();
		if (this.loadingMore || !this.hasMore || this.pendingPinChanges > 0) return;
		const listRevision = this.listRevision;
		// A background refresh (the poll, a new-mail push) keeps the pages already loaded, so it
		// doesn't void this one; only a switch to another folder or filter does.
		const pagingKey = this.pagingKey;
		if (pagingKey !== this.pagedList) return;
		this.loadingMore = true;
		this.loadMoreFailed = false;
		try {
			const response = await http.get<ListResponse>(Endpoints.KONVO_MAIL_RECENT, {
				query: {...this.folderQuery, view: this.view === 'all' ? undefined : this.view, skip: this.nextSkip},
				...RETRY_RATE_LIMITS,
			});
			if (pagingKey !== this.pagedList || this.activeQuery || listRevision !== this.listRevision) return;
			if (response.body?.error) throw new Error(response.body.error);
			runInAction(() => {
				const listed = response.body?.messages ?? [];
				const known = new Set(this.messages.map((message) => message.id));
				const fresh = this.withReadEdits(this.withoutPendingMoves(listed).filter((message) => !known.has(message.id)));
				this.messages = [...this.messages, ...fresh];
				this.rememberInbox();
				// A page of mail already listed (newer mail pushed it down) still moves on to the next one.
				this.hasMore = response.body?.has_more === true && listed.length > 0;
				this.nextSkip = response.body?.next_skip ?? this.nextSkip + listed.length;
			});
		} catch (error) {
			logger.warn('Failed to load more mail', error);
			if (pagingKey === this.pagedList)
				runInAction(() => {
					this.loadMoreFailed = true;
				});
		} finally {
			runInAction(() => {
				this.loadingMore = false;
			});
		}
	}

	/** Takes a moved mail out of the Top results; the returned undo puts it back for the same search. */
	private takeTopResult(messageId: string): (() => void) | null {
		const index = this.topResults.findIndex((message) => message.id === messageId);
		const message = this.topResults[index];
		if (!message) return null;
		const query = this.activeQuery;
		this.topResults = this.topResults.filter((entry) => entry.id !== messageId);
		return () => {
			if (query !== this.activeQuery || this.topResults.some((entry) => entry.id === messageId)) return;
			const at = Math.min(index, this.topResults.length);
			this.topResults = [...this.topResults.slice(0, at), message, ...this.topResults.slice(at)];
		};
	}

	private patchSummary(messageId: string, patch: Partial<KonvoMailMessage>): void {
		const index = this.messages.findIndex((message) => message.id === messageId);
		if (index >= 0) this.messages[index] = {...this.messages[index]!, ...patch};
		const inboxIndex = this.inboxSnapshot?.findIndex((message) => message.id === messageId) ?? -1;
		if (inboxIndex >= 0 && this.inboxSnapshot)
			this.inboxSnapshot[inboxIndex] = {...this.inboxSnapshot[inboxIndex]!, ...patch};
		const topIndex = this.topResults.findIndex((message) => message.id === messageId);
		if (topIndex >= 0) this.topResults[topIndex] = {...this.topResults[topIndex]!, ...patch};
		const detail = this.details.get(messageId);
		if (detail) this.details.set(messageId, {...detail, ...patch});
		// The same mail in a loaded conversation (a row's dropdown, the reader's thread).
		for (const [conversationId, messages] of this.conversations) {
			if (!messages.some((message) => message.id === messageId)) continue;
			this.conversations.set(
				conversationId,
				messages.map((message) => (message.id === messageId ? {...message, ...patch} : message)),
			);
		}
	}

	async markRead(messageId: string, isRead: boolean): Promise<void> {
		const before = this.summaryFor(messageId);
		const wasRead = before?.is_read ?? isRead;
		const inInbox = this.inboxMessages.some((message) => message.id === messageId);
		const requestedDelta = inInbox && wasRead !== isRead ? (isRead ? -1 : 1) : 0;
		const nextCount = Math.max(0, this.unreadCount + requestedDelta);
		const countDelta = nextCount - this.unreadCount;
		const countVersion = this.unreadCountVersion;
		const edit: ReadEdit = {isRead, until: null, countApplied: 0};
		this.readEdits.set(messageId, edit);
		this.patchSummary(messageId, {is_read: isRead});
		this.unreadCount = nextCount;
		try {
			const response = await http.post<{ok?: boolean}>(Endpoints.KONVO_MAIL_MESSAGE_READ(messageId), {
				body: {is_read: isRead},
			});
			if (!response.body?.ok) throw new Error('mark_read_failed');
			// A list already on its way, or one Outlook answers before it settles, can still say the old state.
			runInAction(() => {
				if (this.readEdits.get(messageId) === edit) edit.until = Date.now() + READ_SETTLE_MS;
			});
		} catch (error) {
			logger.warn('Failed to update read state', error);
			runInAction(() => {
				const current = this.readEdits.get(messageId) === edit;
				if (current) this.readEdits.delete(messageId);
				// A later read/unread of the same mail owns its state now.
				if (!current) return;
				this.patchSummary(messageId, {is_read: wasRead});
				if (countVersion === this.unreadCountVersion) this.unreadCount = Math.max(0, this.unreadCount - countDelta);
				// A newer server total already accounts for this failed write, unless that list applied it.
				else this.unreadCount = Math.max(0, this.unreadCount - edit.countApplied);
			});
		}
	}

	/** The read state the person chose wins over a list fetched before Outlook applied it. */
	private withReadEdits(messages: Array<KonvoMailMessage>): Array<KonvoMailMessage> {
		if (this.readEdits.size === 0) return messages;
		const now = Date.now();
		return messages.map((message) => {
			const edit = this.readEdits.get(message.id);
			if (!edit) return message;
			if (edit.until !== null && (now > edit.until || message.is_read === edit.isRead)) {
				// Settled: Outlook agrees, or has had long enough that a different state is someone else's change.
				this.readEdits.delete(message.id);
				return message;
			}
			return message.is_read === edit.isRead ? message : {...message, is_read: edit.isRead};
		});
	}

	/** The change to a listed inbox total for edits the list does not reflect yet; records it per edit. */
	private readEditCountDelta(listed: ReadonlyArray<KonvoMailMessage>, inbox: boolean): number {
		let delta = 0;
		for (const edit of this.readEdits.values()) edit.countApplied = 0;
		if (!inbox) return 0;
		for (const message of listed) {
			const edit = this.readEdits.get(message.id);
			if (!edit || message.is_read === edit.isRead) continue;
			edit.countApplied = edit.isRead ? -1 : 1;
			delta += edit.countApplied;
		}
		return delta;
	}

	toggleRead(messageId: string): void {
		const message = this.summaryFor(messageId);
		if (message) void this.markRead(messageId, !message.is_read);
	}

	async setFlagged(messageId: string, flagged: boolean): Promise<void> {
		this.patchSummary(messageId, {is_flagged: flagged});
		try {
			await http.post(Endpoints.KONVO_MAIL_MESSAGE_FLAG(messageId), {body: {flagged}});
		} catch (error) {
			logger.warn('Failed to update flag', error);
			runInAction(() => this.patchSummary(messageId, {is_flagged: !flagged}));
		}
	}

	/** Apply pins immediately, write them in order, and refresh after the queue settles. */
	async setPinned(messageId: string, pinned: boolean): Promise<PinResult> {
		const wasPinned = (this.summaryFor(messageId) ?? this.details.get(messageId))?.is_pinned ?? false;
		const revision = ++this.listRevision;
		this.pinEdits.set(messageId, {revision, confirmed: this.pinEdits.get(messageId)?.confirmed ?? wasPinned});
		this.pendingPinChanges++;
		this.patchSummary(messageId, {is_pinned: pinned});
		if (pinned && !wasPinned) {
			const message = this.messages.find((entry) => entry.id === messageId);
			if (message) this.messages = [message, ...this.messages.filter((entry) => entry.id !== messageId)];
			const inboxMessage = this.inboxSnapshot?.find((entry) => entry.id === messageId);
			if (inboxMessage && this.inboxSnapshot)
				this.inboxSnapshot = [inboxMessage, ...this.inboxSnapshot.filter((entry) => entry.id !== messageId)];
		}
		const rollback = () =>
			runInAction(() => {
				const edit = this.pinEdits.get(messageId);
				// A failed older write must not undo the user's latest choice.
				if (edit?.revision === revision) this.patchSummary(messageId, {is_pinned: edit.confirmed});
			});
		let write: Promise<unknown> | null = null;
		try {
			const send = () =>
				http.post<{ok?: boolean; error?: string}>(Endpoints.KONVO_MAIL_MESSAGE_PIN(messageId), {
					body: {pinned},
					mode: 'silent',
				});
			// The API rewrites the user's entire pin list, so different messages also share this queue.
			const request = this.pinWrite ? this.pinWrite.catch(() => {}).then(send) : send();
			this.pinWrite = write = request;
			const response = await request;
			if (response.body?.ok) {
				const edit = this.pinEdits.get(messageId);
				if (edit) edit.confirmed = pinned;
				return 'ok';
			}
			rollback();
			return response.body?.error === 'too_many_pins' ? 'too_many' : 'failed';
		} catch (error) {
			logger.warn('Failed to update pin', error);
			rollback();
			return 'failed';
		} finally {
			if (this.pinEdits.get(messageId)?.revision === revision) this.pinEdits.delete(messageId);
			if (this.pinWrite === write) this.pinWrite = null;
			this.pendingPinChanges--;
			if (this.pendingPinChanges === 0) void this.refresh({silent: true});
		}
	}

	async move(messageId: string, folder: KonvoMailFolder): Promise<boolean> {
		if (this.movingMessages.has(messageId) || this.pendingMoves.has(messageId)) return false;
		this.listRevision++;
		this.movingMessages.add(messageId);
		const listFolder = this.folder;
		const listKey = this.listKey;
		const index = this.messages.findIndex((message) => message.id === messageId);
		const inboxIndex = this.inboxSnapshot?.findIndex((message) => message.id === messageId) ?? -1;
		const message = this.summaryFor(messageId);
		const countsUnread = message?.is_read === false && (inboxIndex >= 0 || (listFolder === 'inbox' && index >= 0));
		const top = this.takeTopResult(messageId);
		runInAction(() => {
			this.messages = this.messages.filter((entry) => entry.id !== messageId);
			if (this.inboxSnapshot) this.inboxSnapshot = this.inboxSnapshot.filter((entry) => entry.id !== messageId);
			if (this.selectedId === messageId) this.selectedId = null;
			if (countsUnread) this.unreadCount = Math.max(0, this.unreadCount - 1);
		});
		try {
			const response = await http.post<{ok?: boolean}>(Endpoints.KONVO_MAIL_MESSAGE_MOVE(messageId), {
				body: {folder},
			});
			if (!response.body?.ok) throw new Error('move_failed');
			runInAction(() => this.details.delete(messageId));
			return true;
		} catch (error) {
			logger.warn('Failed to move message', error);
			runInAction(() => {
				// Restore this message alone; concurrent moves and edits keep their results.
				if (
					message &&
					index >= 0 &&
					this.listKey === listKey &&
					!this.messages.some((entry) => entry.id === messageId)
				) {
					const at = Math.min(index, this.messages.length);
					this.messages = [...this.messages.slice(0, at), message, ...this.messages.slice(at)];
				}
				if (
					message &&
					inboxIndex >= 0 &&
					this.inboxSnapshot &&
					!this.inboxSnapshot.some((entry) => entry.id === messageId)
				) {
					const at = Math.min(inboxIndex, this.inboxSnapshot.length);
					this.inboxSnapshot = [...this.inboxSnapshot.slice(0, at), message, ...this.inboxSnapshot.slice(at)];
				}
				if (countsUnread) this.unreadCount += 1;
				top?.();
			});
			return false;
		} finally {
			this.movingMessages.delete(messageId);
			this.listRevision++;
		}
	}

	/**
	 * Takes a message out of the list at once but moves it on the server only
	 * after `delayMs` (MOVE_UNDO_MS by default), so a swipe can be undone.
	 * Returns the undo, or null when the message isn't listed. `onFailed` runs
	 * if the server refuses and the message comes back.
	 */
	deferMove(
		messageId: string,
		target: KonvoMailMoveTarget,
		onFailed?: () => void,
		delayMs = MOVE_UNDO_MS,
	): (() => void) | null {
		const index = this.messages.findIndex((message) => message.id === messageId);
		const message = this.messages[index];
		if (!message) return null;
		this.listRevision++;
		const listFolder = this.folder;
		const listKey = this.listKey;
		const inboxIndex = this.inboxSnapshot?.findIndex((entry) => entry.id === messageId) ?? -1;
		const countsUnread = !message.is_read && (listFolder === 'inbox' || inboxIndex >= 0);
		this.messages = this.messages.filter((candidate) => candidate.id !== messageId);
		if (this.inboxSnapshot) this.inboxSnapshot = this.inboxSnapshot.filter((entry) => entry.id !== messageId);
		if (this.selectedId === messageId) this.selectedId = null;
		if (countsUnread) this.unreadCount = Math.max(0, this.unreadCount - 1);
		const top = this.takeTopResult(messageId);
		const restore = () =>
			runInAction(() => {
				top?.();
				if (inboxIndex >= 0 && this.inboxSnapshot && !this.inboxSnapshot.some((entry) => entry.id === messageId)) {
					const at = Math.min(inboxIndex, this.inboxSnapshot.length);
					this.inboxSnapshot = [...this.inboxSnapshot.slice(0, at), message, ...this.inboxSnapshot.slice(at)];
				}
				if (countsUnread) this.unreadCount += 1;
				if (this.listKey !== listKey || this.messages.some((candidate) => candidate.id === messageId)) return;
				const at = Math.min(index, this.messages.length);
				this.messages = [...this.messages.slice(0, at), message, ...this.messages.slice(at)];
			});
		const commit = async () => {
			if (!this.pendingMoves.delete(messageId)) return;
			this.movingMessages.add(messageId);
			try {
				if (!(await this.sendMove(messageId, target))) throw new Error('move_failed');
				runInAction(() => this.details.delete(messageId));
			} catch (error) {
				logger.warn('Failed to move message', error);
				restore();
				onFailed?.();
			} finally {
				this.movingMessages.delete(messageId);
				this.listRevision++;
			}
		};
		this.pendingMoves.set(messageId, {timer: setTimeout(() => void commit(), Math.max(0, delayMs)), commit});
		return () => {
			const pending = this.pendingMoves.get(messageId);
			if (!pending) return;
			clearTimeout(pending.timer);
			this.pendingMoves.delete(messageId);
			this.listRevision++;
			restore();
		};
	}

	async loadFolders(force = false): Promise<void> {
		if (this.foldersLoading || (this.folders && !force)) return;
		this.foldersLoading = true;
		try {
			const response = await http.get<{folders?: Array<KonvoMailFolderInfo>}>(Endpoints.KONVO_MAIL_FOLDERS);
			runInAction(() => {
				this.folders = response.body?.folders ?? [];
				this.foldersLoadedAt = Date.now();
			});
		} catch (error) {
			logger.warn('Failed to load mail folders', error);
		} finally {
			runInAction(() => {
				this.foldersLoading = false;
			});
		}
	}

	/** The Folders menu opened: load the folders, or refresh their unread counts when a minute old. */
	refreshFoldersIfStale(): void {
		void this.loadFolders(this.folders != null && Date.now() - this.foldersLoadedAt > 60_000);
	}

	/** Puts the mails into one new draft as .eml attachments, for Forward together. */
	async forwardBundle(messageIds: ReadonlyArray<string>): Promise<
		KonvoMailResult<{
			draft_id: string;
			subject: string;
			attachments: Array<{id: string; name: string; size: number; content_type: string}>;
			skipped: Array<{message_id: string; reason: string}>;
		}>
	> {
		return mailCall(
			() => http.post(Endpoints.KONVO_MAIL_FORWARD_BUNDLE, {body: {message_ids: messageIds}, mode: 'silent'}),
			(body) =>
				typeof body['draft_id'] === 'string'
					? {
							draft_id: body['draft_id'],
							subject: String(body['subject'] ?? ''),
							attachments: Array.isArray(body['attachments'])
								? (body['attachments'] as Array<{id: string; name: string; size: number; content_type: string}>)
								: [],
							skipped: Array.isArray(body['skipped'])
								? (body['skipped'] as Array<{message_id: string; reason: string}>)
								: [],
						}
					: null,
		);
	}

	/** Sends every move still inside its undo window. */
	flushPendingMoves(): void {
		for (const pending of [...this.pendingMoves.values()]) {
			clearTimeout(pending.timer);
			void pending.commit();
		}
		this.sendMoveBatches();
	}

	/**
	 * Queues a deferred move so moves that come due together (Tidy up, a
	 * selection) reach the server as one request; the server paces them for
	 * Graph, which throttles a burst of separate moves on one mailbox.
	 */
	private sendMove(messageId: string, target: KonvoMailMoveTarget): Promise<boolean> {
		const key = typeof target === 'string' ? target : `folder:${target.folderId}`;
		return new Promise((resolve) => {
			let batch = this.moveBatches.get(key);
			if (!batch) {
				batch = {target, done: new Map()};
				this.moveBatches.set(key, batch);
			}
			batch.done.set(messageId, resolve);
			this.moveBatchTimer ??= setTimeout(() => this.sendMoveBatches(), MOVE_BATCH_WINDOW_MS);
		});
	}

	private sendMoveBatches(): void {
		if (this.moveBatchTimer) clearTimeout(this.moveBatchTimer);
		this.moveBatchTimer = null;
		const batches = [...this.moveBatches.values()];
		this.moveBatches.clear();
		for (const {target, done} of batches) {
			const ids = [...done.keys()];
			for (let start = 0; start < ids.length; start += MOVE_BATCH_SIZE) {
				const chunk = ids.slice(start, start + MOVE_BATCH_SIZE);
				void this.postMoves(chunk, target).then((moved) => {
					for (const id of chunk) done.get(id)?.(moved.has(id));
				});
			}
		}
	}

	/** The ids the server moved. */
	private async postMoves(ids: Array<string>, target: KonvoMailMoveTarget): Promise<Set<string>> {
		const destination = typeof target === 'string' ? {folder: target} : {folder_id: target.folderId};
		try {
			if (ids.length === 1) {
				const response = await http.post<{ok?: boolean}>(Endpoints.KONVO_MAIL_MESSAGE_MOVE(ids[0]!), {
					body: destination,
				});
				return new Set(response.body?.ok ? ids : []);
			}
			const response = await http.post<{ok?: boolean; results?: Array<{id: string; ok: boolean}>}>(
				Endpoints.KONVO_MAIL_MESSAGES_MOVE,
				{body: {ids, ...destination}},
			);
			const results = response.body?.results;
			if (Array.isArray(results)) return new Set(results.filter((result) => result.ok).map((result) => result.id));
			return new Set(response.body?.ok ? ids : []);
		} catch (error) {
			logger.warn('Failed to move messages', error);
			return new Set();
		}
	}

	private hasPendingMoves(): boolean {
		return this.pendingMoves.size > 0 || this.movingMessages.size > 0;
	}

	private withoutPendingMoves(messages: Array<KonvoMailMessage>): Array<KonvoMailMessage> {
		if (!this.hasPendingMoves()) return messages;
		return messages.filter((message) => !this.pendingMoves.has(message.id) && !this.movingMessages.has(message.id));
	}

	async send(request: KonvoMailSendRequest): Promise<{ok: boolean; error?: string}> {
		try {
			const response = await http.post<{ok?: boolean; error?: string}>(Endpoints.KONVO_MAIL_SEND, {
				body: request,
				mode: 'silent',
			});
			if (response.body?.ok) {
				Activity.track('action.done', {area: 'mail', action: 'mail.send', origin: 'konvo'});
				if (this.folder === 'sent') void this.refresh({silent: true});
				return {ok: true};
			}
			return {ok: false, error: response.body?.error ?? `http_${response.status}`};
		} catch (error) {
			logger.warn('Failed to send mail', error);
			return {ok: false, error: 'network'};
		}
	}

	async reply(
		messageId: string,
		mode: Exclude<KonvoMailComposeMode, 'new'>,
		request: KonvoMailSendRequest,
	): Promise<{ok: boolean; error?: string}> {
		try {
			const response = await http.post<{ok?: boolean; error?: string}>(Endpoints.KONVO_MAIL_MESSAGE_REPLY(messageId), {
				body: {...request, mode},
				mode: 'silent',
			});
			if (response.body?.ok) {
				Activity.track('action.done', {area: 'mail', action: 'mail.send', origin: 'konvo', kind: mode});
				return {ok: true};
			}
			return {ok: false, error: response.body?.error ?? `http_${response.status}`};
		} catch (error) {
			logger.warn('Failed to send reply', error);
			return {ok: false, error: 'network'};
		}
	}

	// ── Drafts: the composer keeps a real Outlook draft up to date while you type ──

	async createDraft(
		request: KonvoMailDraftCreateRequest,
	): Promise<KonvoMailResult<{id: string; web_link: string | null}>> {
		return mailCall(
			() => http.post(Endpoints.KONVO_MAIL_DRAFTS, {body: request, mode: 'silent'}),
			(body) =>
				typeof body['id'] === 'string' ? {id: body['id'], web_link: (body['web_link'] as string) ?? null} : null,
		);
	}

	/** A draft made elsewhere (by the Assistant, say), read back so the composer can continue it. */
	async loadDraft(draftId: string): Promise<KonvoMailResult<KonvoMailSavedDraft>> {
		return mailCall(
			() => http.get(Endpoints.KONVO_MAIL_DRAFT(draftId), {mode: 'silent'}),
			(body) =>
				typeof body['id'] === 'string'
					? {
							id: body['id'],
							subject: typeof body['subject'] === 'string' ? body['subject'] : '',
							to: Array.isArray(body['to']) ? (body['to'] as Array<KonvoMailAddress>) : [],
							cc: Array.isArray(body['cc']) ? (body['cc'] as Array<KonvoMailAddress>) : [],
							bcc: Array.isArray(body['bcc']) ? (body['bcc'] as Array<KonvoMailAddress>) : [],
							body_html: typeof body['body_html'] === 'string' ? body['body_html'] : '',
							in_thread: body['in_thread'] === true,
							attachments: Array.isArray(body['attachments'])
								? (body['attachments'] as KonvoMailSavedDraft['attachments'])
								: [],
						}
					: null,
		);
	}

	async updateDraft(draftId: string, request: KonvoMailDraftRequest): Promise<KonvoMailResult<true>> {
		return mailCall(
			() => http.patch(Endpoints.KONVO_MAIL_DRAFT(draftId), {body: request, mode: 'silent'}),
			() => true,
		);
	}

	async deleteDraft(draftId: string): Promise<KonvoMailResult<true>> {
		return mailCall(
			() => http.delete(Endpoints.KONVO_MAIL_DRAFT(draftId), {mode: 'silent'}),
			() => true,
		);
	}

	async sendDraft(draftId: string, sendAt: string | null): Promise<KonvoMailResult<{scheduled_for: string | null}>> {
		const result = await mailCall(
			() =>
				http.post(Endpoints.KONVO_MAIL_DRAFT_SEND(draftId), {body: sendAt ? {send_at: sendAt} : {}, mode: 'silent'}),
			(body) => ({scheduled_for: (body['scheduled_for'] as string | null | undefined) ?? null}),
		);
		if (result.ok && this.folder === 'sent') void this.refresh({silent: true});
		return result;
	}

	async addDraftAttachment(
		draftId: string,
		attachment: KonvoMailOutgoingAttachment,
	): Promise<KonvoMailResult<{id: string}>> {
		return mailCall(
			() => http.post(Endpoints.KONVO_MAIL_DRAFT_ATTACHMENTS(draftId), {body: attachment, mode: 'silent'}),
			(body) => {
				const id = (body['attachment'] as {id?: unknown} | undefined)?.id;
				return typeof id === 'string' ? {id} : null;
			},
		);
	}

	async startUploadSession(
		draftId: string,
		file: {name: string; content_type: string; size: number},
	): Promise<KonvoMailResult<{session_id: string; chunk_size: number}>> {
		return mailCall(
			() => http.post(Endpoints.KONVO_MAIL_DRAFT_UPLOAD_SESSIONS(draftId), {body: file, mode: 'silent'}),
			(body) =>
				typeof body['session_id'] === 'string'
					? {session_id: body['session_id'], chunk_size: Number(body['chunk_size']) || 3276800}
					: null,
		);
	}

	async uploadChunk(
		draftId: string,
		sessionId: string,
		chunk: {offset: number; total: number; content_base64: string},
	): Promise<KonvoMailResult<{done: boolean; attachment_id: string | null}>> {
		return mailCall(
			() => http.put(Endpoints.KONVO_MAIL_DRAFT_UPLOAD_SESSION(draftId, sessionId), {body: chunk, mode: 'silent'}),
			(body) => ({done: body['done'] === true, attachment_id: (body['attachment_id'] as string | null) ?? null}),
		);
	}

	async removeDraftAttachment(draftId: string, attachmentId: string): Promise<KonvoMailResult<true>> {
		return mailCall(
			() => http.delete(Endpoints.KONVO_MAIL_DRAFT_ATTACHMENT(draftId, attachmentId), {mode: 'silent'}),
			() => true,
		);
	}

	/** Konvo writes the body (plain text) from the mail being answered and the sender's notes. */
	async composeAssist(request: KonvoMailComposeAssistRequest): Promise<KonvoMailResult<{body_text: string}>> {
		return mailCall(
			() => http.post(Endpoints.KONVO_MAIL_COMPOSE_ASSIST, {body: request, mode: 'silent', timeoutMs: 120_000}),
			(body) =>
				typeof body['body_text'] === 'string' && body['body_text'].trim() ? {body_text: body['body_text']} : null,
		);
	}

	async searchContacts(query: string, signal?: AbortSignal): Promise<Array<KonvoMailContact>> {
		try {
			const response = await http.get<{contacts?: Array<KonvoMailContact>}>(Endpoints.KONVO_MAIL_CONTACTS, {
				query: {q: query || undefined},
				signal,
				mode: 'silent',
			});
			return response.body?.contacts ?? [];
		} catch (error) {
			if (!signal?.aborted) logger.warn('Failed to look up contacts', error);
			return [];
		}
	}

	async downloadAttachment(
		messageId: string,
		attachmentId: string,
	): Promise<{name: string; content_type: string; content_base64: string} | null> {
		try {
			const response = await http.get<{name: string; content_type: string; content_base64?: string; error?: string}>(
				Endpoints.KONVO_MAIL_ATTACHMENT(messageId, attachmentId),
				{mode: 'silent'},
			);
			if (!response.body?.content_base64) return null;
			return {
				name: response.body.name,
				content_type: response.body.content_type,
				content_base64: response.body.content_base64,
			};
		} catch (error) {
			logger.warn('Failed to download attachment', error);
			return null;
		}
	}
}

export default new KonvoMailStore();
